import { List, useTable } from "@refinedev/antd";
import { Input, Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";

import { DATE_FORMAT, VERIFICATION_STATUS } from "../labels";
import type { UserItem } from "../types";

const KIND_OPTIONS = [
  { value: "", label: "Tümü" },
  { value: "student", label: "Öğrenci" },
  { value: "tutor", label: "Eğitmen" },
  { value: "both", label: "İkisi de" },
];

function filterValue(filters: ReturnType<typeof useTable>["filters"], field: string) {
  const filter = filters.find((item) => "field" in item && item.field === field);
  return (filter?.value as string | undefined) ?? "";
}

/**
 * /users: every account, student, tutor or both (permissions `students.view` and
 * `tutors.view`). One account can be both (docs/DATABASE.md → Accounts and Roles).
 */
export function UsersPage() {
  const navigate = useNavigate();
  const { tableProps, filters, setFilters } = useTable<UserItem>({
    resource: "users",
    pagination: { mode: "server", pageSize: 25 },
    syncWithLocation: true,
  });
  const kind = filterValue(filters, "kind");
  const search = filterValue(filters, "search");
  const setFilter = (field: "kind" | "search", value: string) =>
    setFilters(
      [
        { field: "kind", operator: "eq", value: field === "kind" ? value : kind },
        { field: "search", operator: "eq", value: field === "search" ? value : search },
      ],
      "replace",
    );

  return (
    <List title="Kullanıcılar">
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={KIND_OPTIONS}
          value={kind}
          onChange={(value) => setFilter("kind", String(value))}
        />
        <Input.Search
          allowClear
          placeholder="Ad veya e-posta"
          defaultValue={search}
          onSearch={(value) => setFilter("search", value)}
          style={{ width: 260 }}
        />
      </Space>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: "max-content" }}
        // The whole row opens the student card, or the tutor card for a tutor-only account; the
        // tags open a specific one.
        onRow={(user) => {
          const card = user.studentId
            ? `/students/${user.studentId}`
            : user.tutor
              ? `/tutors/${user.tutor.id}`
              : null;
          return card ? { onClick: () => navigate(card), style: { cursor: "pointer" } } : {};
        }}
      >
        <Table.Column<UserItem>
          title="Hesap"
          dataIndex="name"
          render={(name: string, user) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{name}</Typography.Text>
              <Typography.Text type="secondary">{user.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<UserItem>
          title="Profiller"
          key="profiles"
          render={(_, user) => (
            <Space wrap>
              {user.studentId ? (
                <Link
                  to={`/students/${user.studentId}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <Tag color="blue">Öğrenci</Tag>
                </Link>
              ) : null}
              {user.tutor ? (
                <Link to={`/tutors/${user.tutor.id}`} onClick={(event) => event.stopPropagation()}>
                  <Tag color={VERIFICATION_STATUS[user.tutor.verificationStatus]?.color}>
                    Eğitmen · {VERIFICATION_STATUS[user.tutor.verificationStatus]?.label}
                  </Tag>
                </Link>
              ) : null}
              {!user.studentId && !user.tutor ? <Tag>Profil yok</Tag> : null}
            </Space>
          )}
        />
        <Table.Column<UserItem>
          title="Doğrulama"
          key="verified"
          render={(_, user) => (
            <Space wrap>
              <Tag color={user.emailVerified ? "green" : "default"}>
                E-posta {user.emailVerified ? "✓" : "✗"}
              </Tag>
              <Tag color={user.phoneNumberVerified ? "green" : "default"}>
                Telefon {user.phoneNumberVerified ? "✓" : "✗"}
              </Tag>
            </Space>
          )}
        />
        <Table.Column<UserItem>
          title="Kayıt"
          dataIndex="createdAt"
          render={(value: string) => dayjs(value).format(DATE_FORMAT)}
        />
      </Table>
    </List>
  );
}
